import { useMemo } from "react";
import { BaseAlertState, AlertStateConfig } from "./BaseAlert.types";
import { getDefaultAlertTitle } from "@/utils/alert-helpers";

export interface UseAlertStateParams extends AlertStateConfig {
  defaultTitle?: string;
  defaultMessage?: string;
  loadingTitle?: string;
  loadingMessage?: string;
}

export const useAlertState = ({
  loading = false,
  hasData = true,
  hasError = false,
  errorMessage,
  successMessage,
  warningMessage,
  infoMessage,
  defaultTitle,
  defaultMessage = "",
  loadingTitle,
  loadingMessage = "Carregando...",
}: UseAlertStateParams): BaseAlertState | null => {
  return useMemo(() => {
    if (loading) {
      return {
        type: "loading" as const,
        title: loadingTitle || getDefaultAlertTitle("loading"),
        message: loadingMessage,
      };
    }

    if (hasError && errorMessage) {
      return {
        type: "error" as const,
        title: getDefaultAlertTitle("error"),
        message: errorMessage,
      };
    }

    if (successMessage) {
      return {
        type: "success" as const,
        title: getDefaultAlertTitle("success"),
        message: successMessage,
      };
    }

    if (warningMessage) {
      return {
        type: "warning" as const,
        title: getDefaultAlertTitle("warning"),
        message: warningMessage,
      };
    }

    if (infoMessage) {
      return {
        type: "info" as const,
        title: defaultTitle || getDefaultAlertTitle("info"),
        message: infoMessage,
      };
    }

    if (!hasData && infoMessage) {
      return {
        type: "info" as const,
        title: defaultTitle || getDefaultAlertTitle("info"),
        message: infoMessage,
      };
    }

    return null;
  }, [
    loading,
    hasData,
    hasError,
    errorMessage,
    successMessage,
    warningMessage,
    infoMessage,
    defaultTitle,
    defaultMessage,
    loadingTitle,
    loadingMessage,
  ]);
};
